import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Check, Copy, KeyRound, Plus, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { BrandButton } from "@/components/brand/brand-button";
import {
	BrandCard,
	BrandCardContent,
	BrandCardDescription,
	BrandCardHeader,
	BrandCardTitle,
} from "@/components/brand/brand-card";
import { BrandInput, BrandInputGroup } from "@/components/brand/brand-input";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { DashboardPageHeader } from "@/components/dashboard/DashboardPageHeader";
import { LoadingScreen } from "@/components/LoadingScreen";
import { ConnectAiDocs } from "@/components/setup/connect-ai-docs";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/dashboard/api-keys")({
	component: ApiKeysPage,
});

const pageShellClass =
	"mx-auto grid w-full max-w-none gap-8 px-4 py-8 sm:px-6 lg:grid-cols-[240px_minmax(0,1fr)] lg:px-8 lg:py-10 xl:max-w-[1800px]";
const sectionCardClass = "bg-card shadow-none";
const sectionHeaderClass =
	"mb-5 flex flex-col gap-2 border-b border-border pb-5 sm:flex-row sm:items-start sm:justify-between";
const sectionIconClass =
	"flex size-9 shrink-0 items-center justify-center rounded-md border border-border bg-background text-muted-foreground";

interface ApiKey {
	id: string;
	name?: string | null;
	keyPrefix: string;
	createdAt: string;
	lastUsedAt?: string | null;
}

interface NewKeyResult {
	keyId: string;
	rawKey: string;
}

function ApiKeysPage() {
	const { session, user, isPending } = useSession();
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [newKeyName, setNewKeyName] = useState("");
	const [creating, setCreating] = useState(false);
	const [createError, setCreateError] = useState<string | null>(null);
	const [newKeyResult, setNewKeyResult] = useState<NewKeyResult | null>(null);
	const [copied, setCopied] = useState(false);
	const [revokingId, setRevokingId] = useState<string | null>(null);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ["mcp-api-keys"],
		queryFn: async () => {
			const res = await fetch("/api/mcp/key", { credentials: "include" });
			if (!res.ok) throw new Error("Failed to load API keys");
			return res.json() as Promise<{ keys: ApiKey[] }>;
		},
		enabled: !!user,
	});

	const keys = data?.keys ?? [];

	const handleCreate = useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();
			setCreating(true);
			setCreateError(null);
			setNewKeyResult(null);
			try {
				const res = await fetch("/api/mcp/key", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ name: newKeyName.trim() || "Default" }),
				});
				const result = await res.json();
				if (!res.ok) {
					setCreateError(result.error || "Failed to create API key");
					return;
				}
				setNewKeyResult({ keyId: result.key.id, rawKey: result.apiKey });
				setNewKeyName("");
				void queryClient.invalidateQueries({ queryKey: ["mcp-api-keys"] });
				await refetch();
			} catch {
				setCreateError("Failed to create API key");
			} finally {
				setCreating(false);
			}
		},
		[newKeyName, queryClient, refetch],
	);

	const handleRevoke = useCallback(
		async (keyId: string) => {
			if (!confirm("Revoke this key? Any agents using it will stop working."))
				return;
			setRevokingId(keyId);
			try {
				const res = await fetch("/api/mcp/key", {
					method: "DELETE",
					headers: { "Content-Type": "application/json" },
					credentials: "include",
					body: JSON.stringify({ keyId }),
				});
				if (!res.ok) {
					const result = await res.json();
					alert(result.error || "Failed to revoke key");
					return;
				}
				if (newKeyResult?.keyId === keyId) setNewKeyResult(null);
				void queryClient.invalidateQueries({ queryKey: ["mcp-api-keys"] });
				await refetch();
			} catch {
				alert("Failed to revoke key");
			} finally {
				setRevokingId(null);
			}
		},
		[newKeyResult, queryClient, refetch],
	);

	const handleCopy = useCallback(async () => {
		if (!newKeyResult) return;
		await navigator.clipboard.writeText(newKeyResult.rawKey);
		setCopied(true);
		setTimeout(() => setCopied(false), 2000);
	}, [newKeyResult]);

	if (isPending) return <LoadingScreen />;

	if (!session || !user) {
		return (
			<div className="min-h-svh flex items-center justify-center bg-background">
				<BrandCard
					variant="elevated"
					padding="lg"
					className="max-w-md text-center"
				>
					<BrandCardHeader>
						<BrandCardTitle>Sign in required</BrandCardTitle>
						<BrandCardDescription>
							Please sign in to manage your API keys
						</BrandCardDescription>
					</BrandCardHeader>
					<BrandCardContent className="pt-6">
						<BrandButton onClick={() => navigate({ to: "/signup" })}>
							Sign In
						</BrandButton>
					</BrandCardContent>
				</BrandCard>
			</div>
		);
	}

	return (
		<DashboardLayout>
			<div className={pageShellClass}>
				<div className="lg:sticky lg:top-24 lg:self-start">
					<DashboardPageHeader
						title="API keys"
						description="Create and revoke keys used by AI agents and external tools."
					/>
				</div>

				<div className="space-y-4">
					{/* Connect AI instructions */}
					<ConnectAiDocs className={sectionCardClass} />

					{/* Create new key */}
					<BrandCard
						variant="default"
						padding="lg"
						className={sectionCardClass}
					>
						<BrandCardHeader className={sectionHeaderClass}>
							<div className="flex items-start gap-3">
								<div className={sectionIconClass}>
									<KeyRound className="size-4" />
								</div>
								<div>
									<BrandCardTitle className="text-base font-semibold tracking-normal">
										Create API key
									</BrandCardTitle>
									<BrandCardDescription className="text-sm leading-6">
										Give it a name to identify which agent or tool uses it.
									</BrandCardDescription>
								</div>
							</div>
						</BrandCardHeader>
						<BrandCardContent>
							<form onSubmit={handleCreate} className="space-y-4">
								<BrandInputGroup label="Key name" className="[&_label]:text-sm">
									<BrandInput
										className="h-9 text-sm"
										value={newKeyName}
										onChange={(e) => setNewKeyName(e.target.value)}
										placeholder="e.g. Claude Desktop, n8n, Cursor"
									/>
								</BrandInputGroup>

								{newKeyResult && (
									<div className="space-y-2 rounded-md border border-border bg-background p-3">
										<p className="text-xs text-muted-foreground">
											Copy your key now. It won't be shown again.
										</p>
										<div className="flex items-center gap-2">
											<code className="min-w-0 flex-1 truncate rounded-md border border-border bg-muted px-3 py-2 font-mono text-xs text-foreground">
												{newKeyResult.rawKey}
											</code>
											<button
												type="button"
												onClick={handleCopy}
												className="shrink-0 rounded-md border border-border p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
												aria-label="Copy API key"
											>
												{copied ? (
													<Check className="size-4 text-foreground" />
												) : (
													<Copy className="size-4" />
												)}
											</button>
										</div>
									</div>
								)}

								{createError && (
									<p className="text-xs text-red-500">{createError}</p>
								)}

								<BrandButton type="submit" size="sm" disabled={creating}>
									{creating ? (
										<>
											<div className="animate-spin h-4 w-4 border-2 border-primary-foreground border-t-transparent rounded-full" />
											Creating...
										</>
									) : (
										<>
											<Plus className="w-4 h-4" />
											Create Key
										</>
									)}
								</BrandButton>
							</form>
						</BrandCardContent>
					</BrandCard>

					{/* Key list */}
					{(isLoading || keys.length > 0) && (
						<BrandCard
							variant="default"
							padding="lg"
							className={sectionCardClass}
						>
							<BrandCardHeader className={sectionHeaderClass}>
								<div>
									<BrandCardTitle className="text-base font-semibold tracking-normal">
										Active keys
									</BrandCardTitle>
									<BrandCardDescription className="text-sm leading-6">
										Keys with access to your AgentReacher account.
									</BrandCardDescription>
								</div>
							</BrandCardHeader>
							<BrandCardContent>
								{isLoading ? (
									<div className="flex items-center justify-center py-6">
										<div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
									</div>
								) : (
									<ul className="divide-y divide-border">
										{keys.map((key) => (
											<li
												key={key.id}
												className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0"
											>
												<div className="min-w-0 flex-1">
													<div className="flex items-center gap-2">
														<span className="text-sm font-medium text-foreground truncate">
															{key.name || "Unnamed key"}
														</span>
														{newKeyResult?.keyId === key.id && (
															<span className="shrink-0 rounded-sm border border-border px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
																new
															</span>
														)}
													</div>
													<div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
														<code className="font-mono">
															{key.keyPrefix}...
														</code>
														<span className="text-border">/</span>
														<span>
															Created{" "}
															{new Date(key.createdAt).toLocaleDateString()}
														</span>
														{key.lastUsedAt && (
															<>
																<span className="text-border">/</span>
																<span>
																	Last used{" "}
																	{new Date(
																		key.lastUsedAt,
																	).toLocaleDateString()}
																</span>
															</>
														)}
													</div>
												</div>
												<button
													type="button"
													onClick={() => handleRevoke(key.id)}
													disabled={revokingId === key.id}
													className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
													title="Revoke key"
												>
													{revokingId === key.id ? (
														<div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
													) : (
														<Trash2 className="w-4 h-4" />
													)}
												</button>
											</li>
										))}
									</ul>
								)}
							</BrandCardContent>
						</BrandCard>
					)}
				</div>
			</div>
		</DashboardLayout>
	);
}
